/**
 * /agent — 高管周报 Agent（PR 9.2m S-tree + 流式 Q&A + PDF 归档）。
 * 左：S-tree 摘要（executive_summary + 6 段折叠）+ 重新生成 + PDF 下载/历史
 * 右：流式问答（POST /api/v2/agent/ask/stream，fetch ReadableStream 逐 chunk）
 *
 * W23 红线（carry-forward 4/5）：not-ready 周不显任何编造摘要/答案;
 * 409 → 「数据采集中」placeholder + 全 disable,**不加 retry loop**。
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Collapse, Empty, Input, Skeleton, Tag, message } from 'antd';
import { DownloadOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';

import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import {
  type CitationsBySection,
  citationChipLabel,
  sectionCitationKey,
  stripInlineTags,
} from '@/lib/agentSummary';
import { api, apiBaseURL } from '@/lib/api';
import {
  DATA_NOT_READY_HINT,
  isAgentNotReadyError,
  isWeekDataReady,
  missingReasonsText,
  parseStreeSections,
} from '@/lib/weekData';
import { useWeekStore } from '@/stores/weekStore';

interface SummaryResp {
  week: string;
  summary_text: string;
  summary_md: string | null;
  citations_by_section?: CitationsBySection; // PR 9.2n 方案A
  summary_schema?: string;
  key_findings: Array<Record<string, unknown>> | null;
  generated_at: string;
  llm_model_version: string;
  llm_provider: string | null;
}

interface ReportItem {
  week: string;
  version: number | null;
  generated_at: string | null;
  pdf_report_url: string | null;
  llm_model_version: string | null;
}

interface ChatTurn {
  role: 'user' | 'agent';
  text: string;
}

/** PR 9.2n:段尾「数据依据」chips(citations,label 由后端解析,prose 0 内联 tag)。 */
function CitationChips({ items }: { items?: CitationsBySection[string] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <span className="text-[11px] text-slate-400">数据依据:</span>
      {items.map((c, i) => (
        <Tag key={`${c.type}-${c.id}-${i}`} color="blue" className="!mx-0 !text-[11px]">
          {citationChipLabel(c)}
        </Tag>
      ))}
    </div>
  );
}

export default function AgentPage() {
  const { week, history } = useWeekStore();
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [chat, setChat] = useState<ChatTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const convIdRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `c-${week}`,
  );

  // PR 9.2m: 本周是否就绪(从 weekStore history 取 readiness,不主动打 LLM endpoint)。
  const weekStatus = history.find((h) => h.week === week)?.status;
  const weekReady = isWeekDataReady(weekStatus);

  // not-ready 时显式 detail(carry-forward 5:0 retry loop;summary query 直接不 enable)。
  const [notReadyDetail, setNotReadyDetail] = useState<string[] | null>(null);
  const notReady = !weekReady || notReadyDetail !== null;

  const summaryQ = useQuery({
    queryKey: ['agent-summary', week],
    queryFn: async () => (await api.get<SummaryResp>('/agent/summary', { params: { week } })).data,
    retry: false,
    enabled: weekReady, // not-ready → 0 fetch 到 /summary
  });

  const reportsQ = useQuery({
    queryKey: ['agent-reports'],
    queryFn: async () =>
      (await api.get<ReportItem[]>('/agent/reports', { params: { limit: 8 } })).data,
    retry: false,
  });

  const regenerate = useMutation({
    mutationFn: async () =>
      (await api.post<SummaryResp>('/agent/summary/generate', null, { params: { week } })).data,
    onSuccess: () => {
      setNotReadyDetail(null);
      qc.invalidateQueries({ queryKey: ['agent-summary', week] });
      qc.invalidateQueries({ queryKey: ['agent-reports'] });
    },
    onError: (err) => {
      const nr = isAgentNotReadyError(err);
      if (nr) setNotReadyDetail(nr.missing_reasons);
      else message.error('摘要生成失败');
    },
  });

  const summary = summaryQ.data;
  const sections = useMemo(() => parseStreeSections(summary?.summary_md), [summary?.summary_md]);
  const execSummary = sections.find((s) => s.heading.includes('执行摘要'))?.body ?? summary?.summary_text ?? '';
  const bodySections = sections.filter((s) => !s.heading.includes('执行摘要'));

  // 流式 Q&A:fetch ReadableStream(EventSource 不支持 POST)。
  const onSend = async () => {
    const q = question.trim();
    if (!q || notReady || streaming) return;
    setChat((h) => [...h, { role: 'user', text: q }, { role: 'agent', text: '' }]);
    setQuestion('');
    setStreaming(true);
    try {
      const resp = await fetch(`${apiBaseURL}/agent/ask/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week, question: q, conversation_id: convIdRef.current }),
      });
      if (resp.status === 409) {
        const body = await resp.json().catch(() => null);
        const reasons = body?.detail?.missing_reasons as string[] | undefined;
        setNotReadyDetail(reasons ?? []);
        setChat((h) => {
          const next = [...h];
          next[next.length - 1] = { role: 'agent', text: missingReasonsText(reasons) };
          return next;
        });
        return;
      }
      if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let acc = '';
      let streamHadError = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split('\n\n');
        buf = blocks.pop() ?? '';
        for (const block of blocks) {
          let event = 'message';
          let dataLine = '';
          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;
          let parsed: Record<string, unknown> = {};
          try {
            parsed = JSON.parse(dataLine);
          } catch {
            continue;
          }
          if (event === 'chunk' && typeof parsed.delta === 'string') {
            acc += parsed.delta;
            setChat((h) => {
              const next = [...h];
              next[next.length - 1] = { role: 'agent', text: acc };
              return next;
            });
          } else if (event === 'error') {
            streamHadError = true;
            const msg = (parsed.message as string) ?? 'Agent 流式错误';
            setChat((h) => {
              const next = [...h];
              next[next.length - 1] = { role: 'agent', text: `（${msg}）` };
              return next;
            });
          }
        }
      }
      if (!acc && !streamHadError) {
        setChat((h) => {
          const next = [...h];
          next[next.length - 1] = { role: 'agent', text: '（未生成回答）' };
          return next;
        });
      }
    } catch {
      setChat((h) => {
        const next = [...h];
        next[next.length - 1] = { role: 'agent', text: '抱歉，Agent 暂时不可用，请稍后再试。' };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const downloadPdf = (params: Record<string, string | number>) => {
    const qs = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    ).toString();
    window.open(`${apiBaseURL}/agent/report.pdf?${qs}`, '_blank');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">
            高管周报 Agent · {week}
          </h1>
          <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
            基于本周数据的 LLM 自动摘要（S-tree 6 段）+ 流式问答
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button
            icon={<DownloadOutlined />}
            disabled={notReady || !summary}
            onClick={() => downloadPdf({ week })}
          >
            下载 PDF
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={regenerate.isPending}
            disabled={notReady}
            onClick={() => regenerate.mutate()}
          >
            重新生成摘要
          </Button>
        </div>
      </div>

      {notReady && (
        <MockBanner message={missingReasonsText(notReadyDetail ?? [])} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 摘要 */}
        <Panel title="本周摘要（S-tree）">
          {notReady ? (
            <div className="py-10 flex items-center justify-center">
              <Empty description={DATA_NOT_READY_HINT} />
            </div>
          ) : summaryQ.isLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : summary ? (
            <div>
              {/* PR 9.2n:执行摘要纯 prose + 数据依据 chips */}
              <div className="text-[13.5px] leading-relaxed text-slate-800 mb-1">
                {stripInlineTags(execSummary)}
              </div>
              <CitationChips items={summary.citations_by_section?.executive_summary} />
              {bodySections.length > 0 && (
                <Collapse
                  className="mt-3"
                  size="small"
                  items={bodySections.map((s, i) => ({
                    key: String(i),
                    label: s.heading,
                    children: (
                      <div>
                        <div className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">
                          {stripInlineTags(s.body)}
                        </div>
                        <CitationChips
                          items={summary.citations_by_section?.[sectionCitationKey(s.heading)]}
                        />
                      </div>
                    ),
                  }))}
                />
              )}
              <div className="mt-4 text-[11px] text-slate-400">
                生成于 {new Date(summary.generated_at).toLocaleString('zh-CN')} · 模型{' '}
                <code>{summary.llm_model_version}</code>
                {summary.llm_provider && <> · {summary.llm_provider}</>}
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-slate-400 py-6 text-center">
              暂无摘要，请点击"重新生成摘要"
            </div>
          )}
        </Panel>

        {/* Q&A */}
        <Panel title="问答（基于本周数据·流式）" noPadding>
          <div className="flex flex-col h-[500px]">
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {chat.length === 0 && (
                <div className="text-[12px] text-slate-400 text-center mt-10">
                  例："广汽埃安风险如何？""本周有几条召回？"
                </div>
              )}
              {chat.map((t, i) => (
                <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                      t.role === 'user' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    {t.text || (streaming && i === chat.length - 1 ? '思考中…' : '')}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 p-3 flex gap-2">
              <Input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onPressEnter={onSend}
                placeholder={notReady ? '本周数据采集中，暂不可问答' : '向 Agent 提问...'}
                disabled={notReady || streaming}
              />
              <Button type="primary" icon={<SendOutlined />} onClick={onSend} loading={streaming} disabled={notReady}>
                发送
              </Button>
            </div>
          </div>
        </Panel>
      </div>

      {/* PDF 历史归档 */}
      <Panel title="周报 PDF 归档（最近 8 期）">
        {reportsQ.data && reportsQ.data.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {reportsQ.data.map((r) => (
              <div key={`${r.week}-${r.version}`} className="flex items-center justify-between py-2.5">
                <div className="text-[13px] text-slate-700">
                  <span className="font-medium">{r.week}</span>
                  {r.version != null && <span className="text-slate-400"> · v{r.version}</span>}
                  {r.generated_at && (
                    <span className="text-[11px] text-slate-400 ml-2">
                      {new Date(r.generated_at).toLocaleString('zh-CN')}
                    </span>
                  )}
                </div>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    downloadPdf(r.version != null ? { week: r.week, version: r.version } : { week: r.week })
                  }
                >
                  下载
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[12px] text-slate-400 py-6 text-center">暂无归档报告</div>
        )}
      </Panel>
    </div>
  );
}
